import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { useFileUploadModel } from './file-upload.model';
import { useState, useRef } from 'react';
import { Actions } from '@client/new/helpers/types';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { validateImage } from '../../pages/creator/extras/viewModel/extras.validation';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { fixImageOrientation } from './file-upload.utils';

type BannerImageVMActionMap = {
  StartBannerUpload: null;
  Remove: null;
  UploadBanner: File;
  AddBanner: string;
};

type BannerVMActions = Actions<BannerImageVMActionMap>;

type FileUploadParams = {
  onChange: (url: string) => void;
};

export const useFileUploadViewModel = (p: FileUploadParams) => {
  const creatorId = useInfluencerId();

  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const { uploadImage } = useFileUploadModel({ creatorId });

  const _dispatch = async (a: BannerVMActions) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'UploadBanner': {
        if (isUploading) return;
        setIsUploading(true);
        setError(null);

        const v = await validateImage(a.payload);

        if (v.error) {
          setError(v.error || 'Invalid image format or size.');
          setIsUploading(false);
          return;
        }

        try {
          const fixedFile = await fixImageOrientation(a.payload);
          const url = await uploadImage(fixedFile);
          p.onChange(url);
        } catch (e) {
          setError('Failed to upload image. Please try again.');
        } finally {
          setIsUploading(false);
        }

        return;
      }
      case 'StartBannerUpload': {
        if (!uploadInputRef.current) return;
        uploadInputRef.current.click();
        break;
      }
      case 'Remove': {
        p.onChange('');
        return;
      }
    }
  };

  const dispatchers = getDispatchers<BannerImageVMActionMap>(_dispatch);

  return {
    isUploading,
    uploadInputRef,
    error,
    ...dispatchers,
  };
};
